import { apiError } from "../../../../../../../../utils/formErrors";
import StartNoticeDialog from "../../../../../../../../component/StartNoticeDialog";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router";
import { Box, Stack, CircularProgress, Dialog, TextField, MenuItem, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../../../component/SecondaryButton";
import SectionLoader from "../../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../../component/EmptyState";
import { getPositionHistory, confirmEmployee, setLifecycleStatus, promoteEmployee, requestExit, cancelExitRequest } from "../../../../../../../../services/employee";
import ConfirmExitDialog from "../../../../../../../../component/ConfirmExitDialog";
import InlineMessage from "../../../../../../../../component/InlineMessage";
import { getGradeLevels } from "../../../../../../../../services/gradeLevel";
import RecordStat, { RecordStatRow } from "../RecordStat";
import { ensureOrgDepartments } from "../../../../../../../../services/useOrgData";
import { getDesignations } from "../../../../../../../../services/designation";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../../../theme";

const money = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "");

const STATUS_META = {
  probation: { label: "On probation", color: tokens.warningText },
  confirmed: { label: "Confirmed", color: tokens.successText },
  notice_period: { label: "Serving notice", color: tokens.warningText },
  exited: { label: "Exited", color: tokens.danger },
};

const TYPE_META = {
  initial: { label: "Joined", color: tokens.textMuted },
  promotion: { label: "Promotion", color: tokens.successText },
  transfer: { label: "Transfer", color: tokens.accent },
  demotion: { label: "Demotion", color: tokens.danger },
  reassignment: { label: "Role change", color: tokens.accent },
  confirmation: { label: "Confirmed", color: tokens.successText },
  status_change: { label: "Status change", color: tokens.textMuted },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
/**
 * One line of a career entry: what the role was.
 *
 * `department` on a PositionHistory snapshot is a department ID, copied from
 * the employee at the time — so joining it straight in printed
 * "DevOps Engineer · 6a9834e8f7d6d004736ff919" in the timeline. It takes the
 * department list so it can say the name, and falls back to nothing rather
 * than to the id: a department that has since been deleted should leave the
 * line reading "DevOps Engineer", not quoting a number at somebody.
 */
const posLine = (p, departments = []) => {
  const dept = p?.department
    ? departments.find((d) => String(d?._id) === String(p.department))?.name
    : null;
  return [p?.designation, p?.grade, dept].filter(Boolean).join(" · ") || "—";
};

const Chip = ({ label, color }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", px: "12px", py: "5px", borderRadius: "999px", background: tokens.surfaceSubtle, border: `1px solid ${tokens.border}` }}>
    <Box sx={{ width: "0.4375rem", height: "0.4375rem", borderRadius: "50%", background: color }} />
    <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>{label}</Box>
  </Box>
);

const ProfileCareer = () => {
  const notify = useNotify();
  const [employeeData, employeeId] = useOutletContext();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  // A resignation on file but not yet acted on. Held locally as well as read
  // from the parent, because accepting or declining one has to change this
  // screen immediately and the parent's employee fetch is not ours to trigger.
  const [pendingExit, setPendingExit] = useState(employeeData?.exitRequest || null);
  const [status, setStatus] = useState(employeeData?.employmentStatus || "confirmed");
  const [promoteOpen, setPromoteOpen] = useState(false);
  const emptyForm = { changeType: "promotion", designation: "", department: "", grade: "", newCtc: "", reason: "" };
  const [form, setForm] = useState(emptyForm);
  const [grades, setGrades] = useState([]);
  const [designations, setDesignations] = useState([]);
  // Employee.department holds a department ID. The field below was free text
  // bound straight to it, so it showed an ObjectId and saved whatever was
  // typed — a name where an id belongs, which breaks the lookup for that
  // person everywhere else. A list, like Grade above it.
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    getGradeLevels().then((r) => setGrades(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    getDesignations().then((r) => setDesignations(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    ensureOrgDepartments().then((d) => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // picking a designation auto-fills its default grade (name) when one is mapped
  const pickDesignation = (name) => {
    const d = designations.find((x) => x.name === name);
    const g = d?.grade && grades.find((x) => String(x._id) === String(d.grade));
    setForm((f) => ({ ...f, designation: name, ...(g ? { grade: g.name } : {}) }));
  };
  const selectedGrade = grades.find((g) => g.name === form.grade);
  const bandText = selectedGrade && (selectedGrade.band?.minCTC || selectedGrade.band?.maxCTC)
    ? `Band: ${money(selectedGrade.band?.minCTC) || "—"} – ${money(selectedGrade.band?.maxCTC) || "—"}` : "";
  const outOfBand = selectedGrade && form.newCtc !== "" && (
    (selectedGrade.band?.minCTC && Number(form.newCtc) < selectedGrade.band.minCTC) ||
    (selectedGrade.band?.maxCTC && Number(form.newCtc) > selectedGrade.band.maxCTC));

  const fetchData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true); setError(false);
    try {
      const res = await getPositionHistory(employeeId);
      if (res?.success === false) throw apiError(res);
      setHistory(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load career timeline"); }
    finally { setLoading(false); }
  }, [employeeId, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setStatus(employeeData?.employmentStatus || "confirmed"); }, [employeeData]);
  useEffect(() => { setPendingExit(employeeData?.exitRequest || null); }, [employeeData]);

  const doConfirm = async () => {
    setBusy(true);
    try {
      const res = await confirmEmployee(employeeId);
      if (res?.success === false) throw apiError(res);
      setStatus("confirmed"); notify.success("Employee confirmed"); fetchData();
    } catch (e) { notify.error(e?.message || "Could not confirm"); }
    finally { setBusy(false); }
  };

  const openPromote = () => {
    setForm({ ...emptyForm, designation: employeeData?.designation || "", department: employeeData?.department || "", grade: employeeData?.grade || "" });
    setPromoteOpen(true);
  };

  const submitPromote = async () => {
    setBusy(true);
    try {
      const payload = {
        changeType: form.changeType,
        designation: form.designation || undefined,
        department: form.department || undefined,
        grade: form.grade || undefined,
        reason: form.reason || undefined,
      };
      if (form.newCtc !== "" && form.newCtc != null) payload.newCtc = Number(form.newCtc);
      const res = await promoteEmployee(employeeId, payload);
      if (res?.success === false) throw apiError(res);
      notify.success("Career change recorded");
      setPromoteOpen(false);
      fetchData();
    } catch (e) { notify.error(e?.message || "Could not record change"); }
    finally { setBusy(false); }
  };

  // Takes what the dialog collected. It used to fire on click with nothing but
  // a status, which left an employee on notice with no end date — nothing for
  // payroll to prorate to and no relieving letter possible, since that letter
  // is mostly the two dates.
  const startNotice = async (details) => {
    try {
      const res = await setLifecycleStatus(employeeId, { status: "notice_period", ...details });
      if (res?.success === false) throw apiError(res);
      setStatus("notice_period");
      setNoticeOpen(false);
      notify.success(
        `On notice until ${new Date(details.lastWorkingDay).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`
      );
      fetchData();
    } catch (e) {
      notify.error(e?.message || "Could not start notice");
    }
  };

  // Recording a resignation without starting notice. The gap between the two
  // is real: somebody hands it in, the company has to agree it, and until then
  // they are working normally on the same status as yesterday.
  const recordExit = async (details) => {
    try {
      const res = await requestExit(employeeId, details);
      if (res?.success === false) throw apiError(res);
      setPendingExit(res?.data || { ...details, requestedAt: new Date().toISOString() });
      setNoticeOpen(false);
      notify.success("Resignation recorded. Nothing starts until it is accepted.");
      fetchData();
    } catch (e) {
      notify.error(e?.message || "Could not record the resignation");
    }
  };

  const declineExit = async () => {
    setBusy(true);
    try {
      const res = await cancelExitRequest(employeeId);
      if (res?.success === false) throw apiError(res);
      setPendingExit(null);
      notify.success("Resignation withdrawn");
      fetchData();
    } catch (e) { notify.error(e?.message || "Could not withdraw it"); }
    finally { setBusy(false); }
  };

  // The last step. The dialog has already shown what is outstanding and taken a
  // reason if anything is, so by here the decision is made.
  const markExited = async ({ force, reason }) => {
    try {
      const res = await setLifecycleStatus(employeeId, { status: "exited", force, reason });
      if (res?.success === false) throw apiError(res);
      setStatus("exited");
      setExitOpen(false);
      notify.success("Marked as left");
      fetchData();
    } catch (e) {
      notify.error(e?.message || "Could not mark as left");
    }
  };

  const sm = STATUS_META[status] || STATUS_META.confirmed;
  const probationDue = status === "probation" && employeeData?.probationEndDate && new Date(employeeData.probationEndDate) <= new Date();

  /**
   * Tenure, and what has happened in it. The timeline below says what changed
   * and when; these say how long and how often, which a timeline makes the
   * reader work out by subtracting dates.
   */
  const stats = useMemo(() => {
    const joined = employeeData?.joiningDate ? new Date(employeeData.joiningDate) : null;
    let tenure = null;
    if (joined && !Number.isNaN(joined.getTime())) {
      const now = new Date();
      let months =
        (now.getUTCFullYear() - joined.getUTCFullYear()) * 12 +
        (now.getUTCMonth() - joined.getUTCMonth());
      // Not a full month until the day of the month has come round.
      if (now.getUTCDate() < joined.getUTCDate()) months -= 1;
      months = Math.max(0, months);
      tenure = { years: Math.floor(months / 12), months: months % 12 };
    }
    const promotions = history.filter((h) => h.changeType === "promotion");
    const roles = new Set(history.map((h) => h.designation).filter(Boolean));
    if (employeeData?.designation) roles.add(employeeData.designation);
    const latest = [...history]
      .filter((h) => h.effectiveDate)
      .sort((a, b) => Date.parse(b.effectiveDate) - Date.parse(a.effectiveDate))[0];
    return { tenure, promotions, roles, latest };
  }, [employeeData, history]);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Career &amp; lifecycle</Box>
          <Chip label={sm.label} color={sm.color} />
          {probationDue && <Chip label="Due for confirmation" color={tokens.warning} />}
        </Stack>

      <RecordStatRow>
        <RecordStat
          label="At the company"
          value={stats.tenure ? `${stats.tenure.years}y ${stats.tenure.months}m` : "—"}
          sub={employeeData?.joiningDate ? `since ${fmtDate(employeeData.joiningDate)}` : "no joining date on file"}
        />
        <RecordStat
          label="Promotions"
          value={stats.promotions.length}
          sub={stats.promotions.length ? `last ${fmtDate(stats.promotions[0]?.effectiveDate)}` : "none recorded"}
        />
        <RecordStat
          label="Roles held"
          value={stats.roles.size || (employeeData?.designation ? 1 : 0)}
          sub={employeeData?.department ? "including the current one" : "current role included"}
        />
        <RecordStat
          label="Last change"
          value={stats.latest ? fmtDate(stats.latest.effectiveDate) : "—"}
          sub={stats.latest?.changeType ? String(stats.latest.changeType).replace(/_/g, " ") : "nothing on record"}
        />
      </RecordStatRow>
        <Stack direction="row" spacing={1}>
          {status !== "exited" && (
            <BtnPrimary onClick={openPromote} disabled={busy} sx={{ borderRadius: "8px" }}>
              Promote / transfer
            </BtnPrimary>
          )}
          {status === "probation" && (
            <BtnSecondary onClick={doConfirm} disabled={busy} sx={{ borderRadius: "8px" }}>
              {busy ? <CircularProgress size={"1rem"} /> : "Confirm"}
            </BtnSecondary>
          )}
          {(status === "confirmed" || status === "probation") && (
            <BtnSecondary onClick={() => setNoticeOpen(true)} disabled={busy} sx={{ borderRadius: "8px" }}>
              {pendingExit ? "Review resignation" : "Resignation / notice"}
            </BtnSecondary>
          )}
          {status === "notice_period" && (
            <BtnSecondary onClick={() => setExitOpen(true)} disabled={busy} sx={{ borderRadius: "8px" }}>
              Mark as left
            </BtnSecondary>
          )}
        </Stack>
      </Stack>

      {/* A resignation sitting unanswered is the kind of thing that gets
          forgotten until the person's last week. It states what has been asked
          for and offers both answers, rather than leaving the decision buried
          in a dialog nobody opens. */}
      {pendingExit && status !== "exited" && status !== "notice_period" && (
        <Box sx={{ mb: "1.25rem" }}>
          <InlineMessage tone="warning" title="Resignation awaiting a decision">
            <Box sx={{ mb: "0.5rem" }}>
              Recorded {fmtDate(pendingExit.requestedAt)}
              {pendingExit.reason ? ` · ${String(pendingExit.reason).replace(/_/g, " ")}` : ""}
              {pendingExit.proposedLastWorkingDay
                ? ` · proposed last day ${fmtDate(pendingExit.proposedLastWorkingDay)}`
                : " · no last day proposed"}
              .
            </Box>
            {pendingExit.note && (
              <Box sx={{ fontStyle: "italic", color: tokens.textMuted, mb: "0.5rem" }}>“{pendingExit.note}”</Box>
            )}
            <Stack direction="row" spacing={1}>
              <BtnPrimary onClick={() => setNoticeOpen(true)} disabled={busy} sx={{ borderRadius: "8px" }}>
                Accept and start notice
              </BtnPrimary>
              <BtnSecondary onClick={declineExit} disabled={busy} sx={{ borderRadius: "8px" }}>
                {busy ? <CircularProgress size={"1rem"} /> : "Withdraw"}
              </BtnSecondary>
            </Stack>
          </InlineMessage>
        </Box>
      )}

      {employeeData?.probationEndDate && status === "probation" && (
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>Probation ends {fmtDate(employeeData.probationEndDate)}.</Box>
      )}

      {loading ? <SectionLoader /> : error ? (
        <EmptyState variant="error" title="Couldn't load timeline" onRetry={fetchData} />
      ) : history.length === 0 ? (
        <EmptyState title="No career history yet" description="Role changes, promotions, transfers and confirmations will appear here." />
      ) : (
        <Box sx={{ position: "relative", pl: "1.5rem" }}>
          <Box sx={{ position: "absolute", left: "0.3125rem", top: "0.5rem", bottom: "0.5rem", width: "2px", background: tokens.border }} />
          <Stack spacing={2.5}>
            {history.map((h, i) => {
              const tm = TYPE_META[h.changeType] || TYPE_META.reassignment;
              return (
                <Box key={h._id || i} sx={{ position: "relative" }}>
                  <Box sx={{ position: "absolute", left: "-1.5rem", top: "0.25rem", width: "0.75rem", height: "0.75rem", borderRadius: "50%", background: tm.color, border: `2px solid ${tokens.surface}`, boxShadow: `0 0 0 2px ${tm.color}33` }} />
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: "0.25rem", flexWrap: "wrap" }}>
                    <Chip label={tm.label} color={tm.color} />
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{fmtDate(h.effectiveDate)}</Box>
                  </Stack>
                  <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
                    {h.statusFrom ? `${STATUS_META[h.statusFrom]?.label || h.statusFrom} → ${STATUS_META[h.statusTo]?.label || h.statusTo}`
                      : h.changeType === "initial" ? posLine(h.to, departments)
                      : `${posLine(h.from, departments)}  →  ${posLine(h.to, departments)}`}
                  </Box>
                  {h.salaryRevision?.changeAmount ? (
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.successText, mt: "0.25rem" }}>
                      CTC {h.salaryRevision.changeAmount >= 0 ? "+" : ""}₹{Number(h.salaryRevision.changeAmount).toLocaleString("en-IN")} ({h.salaryRevision.changePct >= 0 ? "+" : ""}{h.salaryRevision.changePct}%)
                    </Box>
                  ) : null}
                  {(h.reason || h.note) && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>{h.reason || h.note}</Box>}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <Dialog open={promoteOpen} onClose={() => setPromoteOpen(false)} PaperProps={{ sx: { borderRadius: "12px", width: "min(32rem,92vw)" } }}>
        <Box sx={{ p: "1.5rem" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Promote / transfer</Box>
            <IconButton aria-label="Close" size="small" onClick={() => setPromoteOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Type</Box>
          <TextField select fullWidth size="small" value={form.changeType} onChange={(e) => setForm((f) => ({ ...f, changeType: e.target.value }))} sx={{ mb: "1rem" }}>
            {[["promotion", "Promotion"], ["transfer", "Transfer"], ["demotion", "Demotion"], ["reassignment", "Role change"]].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>

          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Designation</Box>
              {designations.length > 0 ? (
                <TextField select fullWidth size="small" value={form.designation} onChange={(e) => pickDesignation(e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {designations.map((d) => <MenuItem key={d._id} value={d.name}>{d.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField fullWidth size="small" value={form.designation} onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))} />
              )}
            </Box>
            <Box sx={{ width: "9rem" }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Grade</Box>
              {grades.length > 0 ? (
                <TextField select fullWidth size="small" value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {grades.map((g) => <MenuItem key={g._id} value={g.name}>{g.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField fullWidth size="small" value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} />
              )}
            </Box>
          </Stack>

          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Department</Box>
          {/* The value is the id, because that is what the field stores; only
              the label is the name. Grade above does the opposite on purpose —
              Employee.grade holds a name. Falls back to free text when no
              departments have been created, rather than offering an empty
              list somebody cannot get past. */}
          {departments.length > 0 ? (
            <TextField select fullWidth size="small" value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} sx={{ mb: "1rem" }}>
              <MenuItem value="">—</MenuItem>
              {departments.map((d) => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
            </TextField>
          ) : (
            <TextField fullWidth size="small" value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} sx={{ mb: "1rem" }} />
          )}

          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>New CTC (optional — applies a salary revision)</Box>
          <TextField fullWidth size="small" type="number" value={form.newCtc} placeholder={employeeData?.designation ? "Leave blank to keep current pay" : ""} onChange={(e) => setForm((f) => ({ ...f, newCtc: e.target.value }))} sx={{ mb: bandText || outOfBand ? "0.375rem" : "1rem" }} />
          {bandText && <Box sx={{ fontSize: "0.8125rem", color: outOfBand ? tokens.warningText : tokens.textMuted, mb: "1rem" }}>{bandText}{outOfBand ? " · CTC is outside this band" : ""}</Box>}

          <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>Reason</Box>
          <TextField fullWidth size="small" multiline minRows={2} value={form.reason} placeholder="e.g. Annual promotion cycle" onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} sx={{ mb: "1.5rem" }} />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <BtnSecondary onClick={() => setPromoteOpen(false)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={submitPromote} disabled={busy} sx={{ borderRadius: "8px" }}>
              {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Apply change"}
            </BtnPrimary>
          </Stack>
        </Box>
      </Dialog>

      <StartNoticeDialog
        employee={pendingExit ? { ...employeeData, exitRequest: pendingExit } : employeeData}
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        onConfirm={startNotice}
        onRequest={recordExit}
      />

      <ConfirmExitDialog
        employee={employeeData}
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        onConfirm={markExited}
      />
    </Box>
  );
};

export default ProfileCareer;
